'use client';

import { HiMapPin, HiBriefcase, HiAcademicCap } from 'react-icons/hi2';
import { Badge } from '@/components/ui/badge';
import { SkillTag } from '@/components/tags';
import type { Job } from '@/types/jobs';
import { Markdown } from '@/components/ui/markdown';
import Link from 'next/link';
import { ShareButtons } from '@/components/common/ShareButtons';
import { Button } from '@/components/ui/button';
import { useCallback, useState, useEffect } from 'react';
import { createJobShareData } from '@/utils/share';
import type { Organisation } from '@/types/organisation';
import { ReferJobModal } from './ReferJobModal';

interface PublicJobDetailsProps {
  job: Job;
  organisation: Organisation;
  onApply: () => void;
}

export function PublicJobDetails({ job, organisation, onApply }: PublicJobDetailsProps) {
  const [hasMounted, setHasMounted] = useState(false);
  const [referModalOpen, setReferModalOpen] = useState(false);

  useEffect(() => {
    try {
      setHasMounted(true);
    } catch (error) {
      console.error('Error in PublicJobDetails useEffect:', error);
    }
  }, []);

  const shareData = useCallback(() => {
    try {
      // Validate required data
      if (!job || !organisation) {
        throw new Error('Missing job or organisation data');
      }

      // Create a minimal organization object with the required fields
      const org: Partial<Organisation> = {
        id: organisation.id,
        name: organisation.name,
      };

      return createJobShareData(job, org as Organisation);
    } catch (error) {
      console.error('Error creating share data:', error);
      return {
        title: job?.title || 'Job Details',
        description: job?.description?.substring(0, 100) + '...' || 'View job details',
        url: typeof window !== 'undefined' ? window.location.href : ''
      };
    }
  }, [job, organisation]);

  const shareDataValue = shareData();

  // Validate required props
  if (!job || !organisation) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="text-center py-12">
          <p className="text-muted-foreground">Invalid job or organization data</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex flex-col gap-6">
        <header>
          <nav className="flex items-center space-x-2 text-sm mb-3">
            <Link
              href={`/careers/${organisation.id}`}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              All Jobs
            </Link>
            <span className="text-muted-foreground">/</span>
            <span className="font-medium text-foreground truncate max-w-[200px]">{job.title}</span>
          </nav>

          {hasMounted ? (
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground">
                {job.title}
              </h1>
              <div className="flex items-center gap-2 sm:gap-4">
                {shareDataValue && <ShareButtons data={shareDataValue} />}

                {job.status === 'ACTIVE' && (
                  <Button
                    variant="outline"
                    onClick={() => setReferModalOpen(true)}
                    className="border-orange-400 text-orange-600 hover:bg-orange-50 hover:text-orange-700 hover:border-orange-500"
                  >
                    Refer someone
                  </Button>
                )}

                {job.status === 'ACTIVE' ? (
                  <Button
                    variant="default"
                    className="btn btn-primary hidden sm:block"
                    onClick={onApply}
                  >
                    Apply Now
                  </Button>
                ) : (
                  <Button variant="outline" className="hidden sm:block" disabled>
                    Application Closed
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <h1 className="text-3xl font-bold tracking-tight text-foreground">{job.title}</h1>
            </div>
          )}
        </header>

        <div className="flex flex-wrap gap-4">
          {job.employmentTypeLabel && (
            <Badge variant="default" className="flex items-center gap-2">
              <HiBriefcase className="w-4 h-4" />
              {job.employmentTypeLabel}
            </Badge>
          )}
          {job.locationTypeLabel && (
            <Badge variant="default" className="flex items-center gap-2">
              <HiMapPin className="w-4 h-4" />
              {job.locationTypeLabel} {job.location && `• ${job.location}`}
            </Badge>
          )}
          {job.workExperience && job.workExperience.minimum && job.workExperience.maximum && (
            <Badge variant="default" className="flex items-center gap-2">
              <HiAcademicCap className="w-4 h-4" />
              {job.workExperience.minimum} - {job.workExperience.maximum} years of work experience
            </Badge>
          )}
        </div>

        {job.skillsLabels && job.skillsLabels.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {job.skillsLabels.map(skill => (
              <SkillTag
                key={skill}
                variant="outline"
                className="px-3 py-1.5 text-sm font-medium text-secondary border-secondary"
              >
                {skill}
              </SkillTag>
            ))}
          </div>
        )}

        <div className="prose max-w-none mb-8 prose-headings:mt-0 prose-p:mt-0 prose-ul:mt-0 prose-ol:mt-0 prose-li:mt-0">
          {job.description ? (
            <Markdown compact>{job.description}</Markdown>
          ) : (
            <p className="text-muted-foreground">No job description available.</p>
          )}
        </div>
      </div>

      <ReferJobModal
        open={referModalOpen}
        onOpenChange={setReferModalOpen}
        jobId={job.id}
        jobTitle={job.title}
        candidateAppBaseUrl={
          process.env.NEXT_PUBLIC_CANDIDATE_APP_URL ?? process.env.NEXT_CANDIDATE_APP_URL ?? ''
        }
      />
    </div>
  );
}
